import styled from 'styled-components';

// Page wrapper with themed background
export const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.background || '#0f172a'
      : 'transparent'};
`;

// Container matching the layout structure
export const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;

  @media (max-width: 768px) {
    padding: 16px;
    max-width: 100%;
    width: 100%;
    overflow-x: hidden;
  }
`;

// Dashboard Layout with sidebar
export const DashboardLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 280px;
  gap: 24px;
  align-items: start;
  width: 100%;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    display: block;
    width: 100%;
  }
`;

export const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

export const Sidebar = styled.div`
  position: sticky;
  top: 20px;

  @media (max-width: 1200px) {
    position: static;
    width: 100%;
    margin-top: 16px;
  }

  @media (max-width: 768px) {
    position: static;
    width: 100%;
    margin-top: 16px;
  }
`;

// Header Container
export const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    align-items: center;
    gap: 12px;
  }
`;

export const HeaderTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  margin: 0;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

// Content wrapper for the table card
export const ContentWrapper = styled.div`
  padding: 24px;
  font-family: Arial, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI',
    Roboto, 'Helvetica Neue', Ubuntu, sans-serif;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

// Page Header with search and filters
export const PageHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

// Filter row for dropdowns
export const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }
`;

// Filter container for each dropdown
export const FilterContainer = styled.div`
  min-width: 180px;

  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
  }
`;

// Status badge styling
export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;

  ${(props) => {
    switch (props.status?.toLowerCase()) {
      case 'active':
        return `
          background: ${
            props.theme.name === 'dark'
              ? 'rgba(34, 197, 94, 0.15)'
              : 'rgba(34, 197, 94, 0.1)'
          };
          color: ${props.theme.name === 'dark' ? '#4ade80' : '#16a34a'};
        `;
      case 'inactive':
      case 'expired':
        return `
          background: ${
            props.theme.name === 'dark'
              ? 'rgba(239, 68, 68, 0.15)'
              : 'rgba(239, 68, 68, 0.1)'
          };
          color: ${props.theme.name === 'dark' ? '#f87171' : '#dc2626'};
        `;
      case 'pending':
        return `
          background: ${
            props.theme.name === 'dark'
              ? 'rgba(234, 179, 8, 0.15)'
              : 'rgba(234, 179, 8, 0.1)'
          };
          color: ${props.theme.name === 'dark' ? '#fbbf24' : '#ca8a04'};
        `;
      default:
        return `
          background: ${
            props.theme.name === 'dark'
              ? 'rgba(148, 163, 184, 0.15)'
              : 'rgba(148, 163, 184, 0.1)'
          };
          color: ${props.theme.name === 'dark' ? '#94a3b8' : '#64748b'};
        `;
    }
  }}
`;

// Ticket name styled to match primary color
export const TicketName = styled.span`
  color: ${(props) => props.theme?.colors?.primary || '#FA4616'};
  font-weight: 500;
`;

// Back button
export const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)'};
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-right: 12px;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.1)'};
  }
`;
